import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ClipboardText, TickCircle, ArrowDown2 } from "iconsax-react";
import { EmptyState, Pagination } from "@/shared/components";
import { useAuth, useLazyMount } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { Prompt, Button, Dropdown, DropdownItem } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useCreateItemRequestMutation } from "@/features/accounting/warehouse";
import type { CreateItemRequestDto } from "@/features/accounting/warehouse";
import { useRequestsListPage } from "../../hooks/useRequestsListPage";
import RequestCard from "../../components/RequestCard";
import RequestDetailsModal from "../../components/RequestDetailsModal";
import type { SelfServiceRequest } from "../../types";
import type { RequestView } from "../../utils/selfRequestRules";
import {
  RequestsHeader,
  RequestsFilter,
  RequestsTabs,
  RequestsPageSkeleton,
} from "./components";
import type { RequestsTab } from "./components";

const AmendmentCreateModal = lazy(() =>
  import("../../components/AmendmentCreateModal").then((m) => ({ default: m.default })),
);
const CreateSelfAnnualUnpaidLeaveForm = lazy(() =>
  import("../../components/CreateSelfAnnualUnpaidLeaveForm").then((m) => ({ default: m.default })),
);
const CreateSelfMedicalLeaveForm = lazy(() =>
  import("../../components/CreateSelfMedicalLeaveForm").then((m) => ({ default: m.default })),
);
const TerminationCreateModal = lazy(() =>
  import("../../components/TerminationCreateModal").then((m) => ({ default: m.default })),
);
const CreateItemRequestModal = lazy(() =>
  import("@/features/accounting/warehouse/components/CreateRequestModal").then((m) => ({
    default: m.default,
  })),
);
const CreateAdvanceRequestForm = lazy(() =>
  import("../../components/CreateAdvanceRequestForm").then((m) => ({ default: m.default })),
);

export default function RequestsPage() {
  const { t } = useTranslation("EmployeePortal");
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canCreateEmployeeRequests = user?.user_type === "worker" || user?.has_worker_profile === true;
  const workerStatus = user?.profile?.worker_profile?.status ?? user?.profile?.status;
  const isHiringIncomplete = workerStatus === "pending_documents" || workerStatus === "pending_approval";

  const {
    data,
    isLoading,
    isError,
    refetch,
    view,
    setView,
    filteredRequests,
    pagination,
    pageRequests,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    hasActiveFilters,
    handleResetFilters,
    stats,
    locale,
  } = useRequestsListPage();

  const [selectedRequest, setSelectedRequest] = useState<SelfServiceRequest | null>(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [showAnnualLeaveModal, setShowAnnualLeaveModal] = useState(false);
  const [showMedicalLeaveModal, setShowMedicalLeaveModal] = useState(false);
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [showItemRequestModal, setShowItemRequestModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const annualLeaveWasOpened = useLazyMount(showAnnualLeaveModal);
  const medicalLeaveWasOpened = useLazyMount(showMedicalLeaveModal);
  const terminationWasOpened = useLazyMount(showTerminationModal);
  const itemRequestWasOpened = useLazyMount(showItemRequestModal);
  const advanceWasOpened = useLazyMount(showAdvanceModal);
  const [createItemRequest, { isLoading: isCreatingItemRequest }] = useCreateItemRequestMutation();
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const tabs: RequestsTab[] = [
    { id: "active", label: t("requests.view.active"), icon: ClipboardText, count: stats.totalActive },
    { id: "history", label: t("requests.view.history"), icon: TickCircle },
  ];

  async function handleCreateItemRequest(dto: CreateItemRequestDto) {
    try {
      await createItemRequest(dto).unwrap();
      setShowItemRequestModal(false);
      setPrompt({
        title: t("requests.createItemRequestSuccess"),
        text: t("requests.createItemRequestSuccessMessage"),
        variant: "success",
      });
    } catch (err: unknown) {
      setShowItemRequestModal(false);
      setPrompt({
        title: t("requests.createItemRequestError"),
        text: extractErrorMessage(err, "requests.createItemRequestErrorMessage", t),
        variant: "error",
      });
    }
  }

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.requestsTitle")}</title>
        <section className="flex-1 min-w-0">
          <RequestsHeader />
          <RequestsPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.requestsTitle")}</title>
        <section className="flex-1 min-w-0">
          <RequestsHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("requests.messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.requestsTitle")}</title>
      <section className="flex-1 min-w-0">
        <RequestsHeader>
          <Dropdown
            open={showCreateDropdown}
            onClose={() => setShowCreateDropdown(false)}
            align="right">
            <Button
              variant="primary"
              disabled={isHiringIncomplete}
              textDuringDisabled={t("requests.hiringIncompleteTooltip")}
              onClick={() => setShowCreateDropdown(!showCreateDropdown)}>
              {t("requests.createRequest")}
              <ArrowDown2
                size={16}
                color="currentColor"
                className={`transition-transform ${showCreateDropdown ? "rotate-180" : ""}`}
              />
            </Button>
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowAnnualLeaveModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("requests.createAnnualLeave")}
              </DropdownItem>
            )}
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowMedicalLeaveModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("requests.createMedicalLeave")}
              </DropdownItem>
            )}
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowAmendmentModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("requests.createAmendment")}
              </DropdownItem>
            )}
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowTerminationModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("requests.createTermination")}
              </DropdownItem>
            )}
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowCreateDropdown(false);
                  navigate("/self/profile?edit=true");
                }}>
                {t("requests.createDataChange")}
              </DropdownItem>
            )}
            {hasPermission(PERMISSIONS.WAREHOUSE.REQUEST_ITEM) && (
              <DropdownItem
                onClick={() => {
                  setShowItemRequestModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("requests.createItemRequest")}
              </DropdownItem>
            )}
            {canCreateEmployeeRequests && (
              <DropdownItem
                onClick={() => {
                  setShowAdvanceModal(true);
                  setShowCreateDropdown(false);
                }}>
                {t("advance.requestButton")}
              </DropdownItem>
            )}
          </Dropdown>
        </RequestsHeader>

        <RequestsTabs items={tabs} activeId={view} onChange={(id) => setView(id as RequestView)} />

        <div className="radius-3xl surface-base-fill p-7 mt-4">
          <RequestsFilter
            search={search}
            onSearchChange={setSearch}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            statusOptions={statusOptions}
            locale={locale}
            hasActiveFilters={hasActiveFilters}
            onReset={handleResetFilters}
          />

          {filteredRequests.length === 0 ? (
            <EmptyState
              hasActiveFilters={hasActiveFilters}
              noResultsText={t("requests.noResults")}
              noDataText={t("requests.noData")}
              noDataHint={t("requests.noDataHint")}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {pageRequests.map(function renderCard(request) {
                  return (
                    <RequestCard
                      key={`${request.type}_${request.id}`}
                      request={request}
                      locale={locale}
                      onViewDetails={setSelectedRequest}
                    />
                  );
                })}
              </div>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={filteredRequests.length}
              />
            </>
          )}
        </div>
      </section>

      {selectedRequest && (
        <RequestDetailsModal request={selectedRequest} onClose={() => setSelectedRequest(null)} onSuccess={refetch} />
      )}

      {showAmendmentModal && (
        <Suspense fallback={null}>
          <AmendmentCreateModal
            mode="worker"
            onClose={() => setShowAmendmentModal(false)}
            onSuccess={() => {
              setShowAmendmentModal(false);
            }}
          />
        </Suspense>
      )}

      {annualLeaveWasOpened && (
        <Suspense fallback={null}>
          <CreateSelfAnnualUnpaidLeaveForm
            hidden={!showAnnualLeaveModal}
            onClose={() => setShowAnnualLeaveModal(false)}
            onSuccess={() => {
              setShowAnnualLeaveModal(false);
            }}
          />
        </Suspense>
      )}

      {medicalLeaveWasOpened && (
        <Suspense fallback={null}>
          <CreateSelfMedicalLeaveForm
            hidden={!showMedicalLeaveModal}
            onClose={() => setShowMedicalLeaveModal(false)}
            onSuccess={() => {
              setShowMedicalLeaveModal(false);
            }}
          />
        </Suspense>
      )}

      {terminationWasOpened && (
        <Suspense fallback={null}>
          <TerminationCreateModal
            hidden={!showTerminationModal}
            onClose={() => setShowTerminationModal(false)}
            onSuccess={() => {
              setShowTerminationModal(false);
            }}
          />
        </Suspense>
      )}

      {itemRequestWasOpened && (
        <Suspense fallback={null}>
          <CreateItemRequestModal
            hidden={!showItemRequestModal}
            onClose={() => setShowItemRequestModal(false)}
            onConfirm={handleCreateItemRequest}
            isLoading={isCreatingItemRequest}
            title={t("requests.createItemRequest")}
          />
        </Suspense>
      )}

      {advanceWasOpened && (
        <Suspense fallback={null}>
          <CreateAdvanceRequestForm
            hidden={!showAdvanceModal}
            onClose={() => setShowAdvanceModal(false)}
            onSuccess={() => {
              setShowAdvanceModal(false);
              refetch();
            }}
          />
        </Suspense>
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}
